/**
 * The welcome tour Dietech Online shows the first time someone signs in: who sees it and when, the
 * steps each path takes, and the first tasks, which follow what the user's role can open. Pure, so the
 * rules are unit-tested without mounting the layout. The words on each step are in
 * welcomeTourContent.ts and WelcomeTour.tsx.
 */
import { PREF_KEYS } from '../../hooks/useUserPreferences';
import { LEVEL } from '../../hooks/usePermission';
import { allNavPages, buildNavSections, type NavContext } from '../../navigation/navConfig';

/**
 * Raise this to show a new tour to everyone once more, on purpose. Stored per user on the server
 * and in this browser (hooks/useServerUserState), so it is once per person, not per browser.
 */
export const WELCOME_TOUR_VERSION = 1;

/** useUserPreferences key. The value is a WelcomeTourRecord. */
export const WELCOME_TOUR_PREF = PREF_KEYS.WELCOME_TOUR;

export interface WelcomeTourRecord {
  version: number;
  /** When it was shown (ISO). */
  shownAt?: string;
  /** How it ended: finished on the last step, or skipped / closed part way. */
  outcome?: 'finished' | 'skipped';
}

/** The role levels the rules read (SecurityRole). */
export interface WelcomeTourRole {
  dietech_Selective_Menu_Touch_Level?: number | null;
}

/**
 * Pages inside the main layout where the tour never opens on its own: Selective Touch is a
 * touch screen that staff use one resident after another, often on a shared tablet. Tablets
 * (/tablet), bedside screens (/bedside) and the kitchen screen (/kitchen) are outside the main
 * layout, so the tour cannot open there at all.
 */
const NO_TOUR_PREFIXES = ['/selective-touch'];

function onNoTourPage(pathname: string): boolean {
  return NO_TOUR_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Selective Touch level 1 or 2 ("Direct Load"): the employee who signs in straight to Selective
 * Touch's employee list and never uses the rest of the website (auth/resolveStartRoute.ts).
 */
export function isTouchDirectLoad(role: WelcomeTourRole | null | undefined): boolean {
  const touch = role?.dietech_Selective_Menu_Touch_Level ?? 0;
  return touch === 1 || touch === 2;
}

/** Whether the stored record means this version has been seen (finished, skipped or closed). */
export function tourSeen(record: WelcomeTourRecord | null | undefined): boolean {
  return !!record && typeof record.version === 'number' && record.version >= WELCOME_TOUR_VERSION;
}

/**
 * Whether the tour opens by itself now. Only once the user, facility and role have loaded, never on
 * a touch page or for a Direct Load employee, and only while this version has not been seen.
 */
export function shouldAutoShowTour(args: {
  ready: boolean;
  pathname: string;
  role: WelcomeTourRole | null | undefined;
  record: WelcomeTourRecord | null | undefined;
}): boolean {
  if (!args.ready || !args.role) return false;
  if (!tourMayOpenHere({ pathname: args.pathname, role: args.role })) return false;
  return !tourSeen(args.record);
}

/**
 * Whether anything that opens by itself (the tour, the What's New pop-up) may open on this page for
 * this user: never on a touch page or for a Direct Load employee.
 */
export function tourMayOpenHere(args: { pathname: string; role: WelcomeTourRole | null | undefined }): boolean {
  if (isTouchDirectLoad(args.role)) return false;
  return !onNoTourPage(args.pathname);
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export type TourPage = 'welcome' | 'same' | 'different' | 'new' | 'job' | 'setup' | 'ready';

/** Has the user used Dietech on the desktop? Null until they answer. */
export type DesktopAnswer = 'desktop' | 'new' | null;

/**
 * The steps, in order. Desktop users get what stays the same and what is different; new users skip
 * them. Until the first step is answered the shorter path shows.
 */
export function tourPages(answer: DesktopAnswer): TourPage[] {
  return answer === 'desktop'
    ? ['welcome', 'same', 'different', 'new', 'job', 'setup', 'ready']
    : ['welcome', 'new', 'job', 'setup', 'ready'];
}

/** The step's name in the step list. */
export function stepName(page: TourPage, answer: DesktopAnswer): string {
  switch (page) {
    case 'welcome': return 'Welcome';
    case 'same': return 'What stays the same';
    case 'different': return 'What\'s different';
    case 'new': return answer === 'desktop' ? 'What\'s new' : 'What you can do';
    case 'job': return 'What to do first';
    case 'setup': return 'Your settings';
    case 'ready': return 'You\'re ready';
  }
}

/**
 * Whether the step at `target` can be opened from the step list: always the current one and any
 * visited; any step at all once the first question is answered (the answer is the only thing later
 * steps depend on).
 */
export function canOpenStep(target: number, args: { answer: DesktopAnswer; visited: number; count: number }): boolean {
  if (target < 0 || target >= args.count) return false;
  if (target <= args.visited) return true;
  return args.answer != null;
}

// ---------------------------------------------------------------------------
// What the role can open
// ---------------------------------------------------------------------------

/** 'view' = the page opens for the role; 'edit' = its role column is at Add/Edit (2) or more. */
export type Need = 'view' | 'edit';

/** Answers "can this user open that page?" from the menu's own rules. */
export type RouteCheck = (route: string, need?: Need) => boolean;

/**
 * The same answer the menu gives (navigation/navConfig buildNavSections, which mirrors the route
 * guards in App.tsx): a page the role, licence and facility allow, and not greyed out (the EHR page
 * when the facility's EHR is off). Feature Access hides are not applied: hiding a page from the menu
 * is not losing the right to it. `route` is the page's config route, as in navConfig.
 */
export function routeCheck(ctx: NavContext): RouteCheck {
  const open = new Set(
    buildNavSections(ctx, { applyHides: false })
      .flatMap((s) => s.pages)
      .filter((p) => !p.disabledReason)
      .map((p) => p.key),
  );
  return (route, need = 'view') => {
    if (!open.has(route)) return false;
    if (need === 'view') return true;
    const page = allNavPages().find((x) => x.page.route === route)?.page;
    if (!page?.securityField) return true;
    return ctx.getLevel(page.securityField) >= Math.max(page.minLevel ?? LEVEL.VIEW, LEVEL.EDIT);
  };
}

// ---------------------------------------------------------------------------
// First tasks
// ---------------------------------------------------------------------------

export interface FirstTask {
  id: string;
  /** The bold name. */
  label: string;
  /** One sentence on what it is, taken from the article's own summary. */
  text: string;
  /** Help article slug (src/help/articles), linked as "Read: <its title>". */
  slug?: string;
  /** Or a page of the website, linked as `linkText`. */
  to?: string;
  linkText?: string;
}

interface TaskRule extends FirstTask {
  /** The page the task is done on (navConfig route), and what the role needs there. */
  page?: string;
  need?: Need;
  /** Only where the facility's EHR is connected. */
  ehr?: boolean;
  /** Only where Getting Started is on and the user may use it (useGettingStartedAccess). */
  gettingStarted?: boolean;
  /** Everyone gets it, always last: it is about their own settings, not a page the role opens. */
  everyone?: boolean;
}

/**
 * Every task, most common daily work first (and setting the facility up first of all, where that is
 * still to do). The list shows the first MAX_TASKS the role can do. Each sentence follows the
 * article's summary. There is no article yet for assessments or plans of care, so they have no task.
 */
export const TASK_RULES: TaskRule[] = [
  { id: 'getting-started', gettingStarted: true, to: '/getting-started', linkText: 'Open Getting Started',
    label: 'Set up your facility',
    text: 'Getting Started takes you through the steps to your first service, and picks up where you left off.' },
  { id: 'find-resident', page: '/people', slug: 'finding-a-resident',
    label: 'Find a resident',
    text: 'Get to any resident in a few keystrokes from any page, or narrow the Residents list to one unit.' },
  { id: 'diet-order', page: '/people', need: 'edit', slug: 'change-diet-order',
    label: 'Change a diet order',
    text: 'Change diet types and consistencies, and see what the new order changes on the meal pattern and tray ticket.' },
  { id: 'selective-touch', page: '/selective-touch', slug: 'selective-touch',
    label: 'Take menu orders with Selective Touch',
    text: "Take each resident's meal choices on a tablet or touch screen, from a menu that already fits their diet and consistency." },
  { id: 'tray-tickets', page: '/reports', slug: 'tray-tickets',
    label: 'Preview and print tray tickets',
    text: "Check a resident's ticket on screen before it prints, print a whole meal in tray line order, and reprint one ticket." },
  { id: 'weights', page: '/weight-entry', slug: 'entering-weights',
    label: 'Enter weights',
    text: 'Enter a round of weights on Weight Entry, and spot the residents whose weight changed enough to need a closer look.' },
  { id: 'admit', page: '/people', need: 'edit', slug: 'admit-resident',
    label: 'Admit a new resident',
    text: 'Add the room, diet order and allergies, and check the first tray ticket before it prints.' },
  { id: 'meal-patterns', page: '/people', need: 'edit', slug: 'meal-patterns',
    label: 'Keep meal patterns',
    text: 'Add what a resident gets at each meal on top of the menu, such as coffee at breakfast or a bedtime snack.' },
  { id: 'ehr-mapping', page: '/emr', ehr: true, slug: 'map-ehr-text',
    label: 'Map the diet order text your EHR sends',
    text: 'Map each phrase once, and Dietech turns it into the right diet, consistency, allergy or special need from then on.' },
  { id: 'menu-cycles', page: '/menucycle', slug: 'menu-cycles',
    label: 'Your menu cycles',
    text: "See your facility's menu cycles, choose which one is active, and copy one to start next season's." },
  { id: 'menu-analysis', page: '/menucycle', slug: 'menu-analysis',
    label: "Check a menu cycle's nutrition",
    text: 'See what each diet order gets every day, in calories, protein, sodium and more, against the daily targets.' },
  { id: 'tray-line', page: '/tray-line-setup', need: 'edit', slug: 'tray-line-setup',
    label: 'Set up the tray line',
    text: 'Put rooms and beds in the order your tray line runs, so tray tickets print in the order trays are made.' },
  { id: 'reports', page: '/reports', slug: 'running-a-report',
    label: 'Run a report',
    text: 'Find a report, choose its options, and print it or save it while you keep working.' },
  { id: 'analysis', page: '/analysis', slug: 'using-analysis',
    label: 'Look across residents with Analysis',
    text: 'Review diet orders, allergies, special needs and eating devices on one page, and see who is behind any number.' },
  { id: 'settings', everyone: true, slug: 'settings-and-preferences',
    label: 'Your settings',
    text: 'Set the facility and page you start on, make the text bigger, or change your password.' },
];

/** At most this many tasks. */
export const MAX_TASKS = 5;

/**
 * The tasks for this user: the first of TASK_RULES whose page the role can open, then the tasks
 * everyone gets (their own settings), MAX_TASKS in all.
 */
export function firstTasks(can: RouteCheck, opts: { ehrActive?: boolean; gettingStarted?: boolean } = {}): FirstTask[] {
  const task = (rule: TaskRule): FirstTask =>
    ({ id: rule.id, label: rule.label, text: rule.text, slug: rule.slug, to: rule.to, linkText: rule.linkText });
  const always = TASK_RULES.filter((rule) => rule.everyone).map(task);
  const out: FirstTask[] = [];
  for (const rule of TASK_RULES) {
    if (out.length >= MAX_TASKS - always.length) break;
    if (rule.everyone) continue;
    if (rule.gettingStarted && !opts.gettingStarted) continue;
    if (rule.ehr && !opts.ehrActive) continue;
    if (rule.page && !can(rule.page, rule.need)) continue;
    out.push(task(rule));
  }
  return [...out, ...always];
}

/** The help article everyone should read first. */
export const START_HERE_SLUG = 'getting-around';

/** A page to open from the last step. */
export interface NextAction {
  label: string;
  to: string;
}

/**
 * The last step's buttons: up to two pages the role opens most (in this order), then Help, which
 * everyone has. `residents` is the facility's word for its residents.
 */
export function nextActions(can: RouteCheck, residents: string): NextAction[] {
  const candidates: (NextAction & { page: string })[] = [
    { page: '/people', to: '/people', label: `Open ${residents}` },
    { page: '/selective-touch', to: '/selective-touch', label: 'Open Selective Touch' },
    { page: '/reports', to: '/reports', label: 'Open Reports' },
    { page: '/menucycle', to: '/menucycle', label: 'Open Menu Cycles' },
  ];
  const picked = candidates.filter((c) => can(c.page)).slice(0, 2).map(({ label, to }) => ({ label, to }));
  return [...picked, { label: 'Open Help', to: '/help' }];
}
